import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

// ---- Types ----

export type LoraTrainingStatus =
  | 'queued'
  | 'training'
  | 'complete'
  | 'failed'
  | 'approved'
  | 'rejected'
  | 'cancelled';

export type TrainingPhase =
  | 'download'
  | 'caption'
  | 'configure'
  | 'training'
  | 'bundling'
  | 'uploading';

export interface LoraModelSummaryDto {
  id: string;
  entityId: string;
  name: string;
  version?: string | null;
  trainingStatus: LoraTrainingStatus;
  triggerWord: string;
  trainer?: string | null;
  qualityScore?: number | null;
  currentPhase?: string | null;
  progress?: number | null;
  requeueCount: number;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

export interface LoraModelDto extends LoraModelSummaryDto {
  modelUrl?: string | null;
  datasetUrl?: string | null;
  sampleImageUrls: string[];
  trainingMetrics?: TrainingMetrics | null;
  hyperparameters?: Hyperparameters | null;
  lastError?: string | null;
  progressMessage?: string | null;
  etaSeconds?: number | null;
  claimedByWorker?: string | null;
}

export interface TrainingMetrics {
  final_loss?: number | null;
  steps_completed?: number | null;
  wall_clock_seconds?: number | null;
  peak_vram_bytes?: number | null;
}

export interface Hyperparameters {
  rank: number;
  steps: number;
  learning_rate: number;
  batch_size: number;
  resolution: number;
  mixed_precision: string;
  save_every_n_steps: number;
  seed: number;
}

export interface QueueTrainingRequest {
  trainer?: string | null;
  hyperparameterOverrides?: Partial<Hyperparameters> | null;
}

export interface TrainingQueueItemDto {
  id: string;
  entityId: string;
  entityName: string;
  entityType: string;
  referenceImageUrl?: string | null;
  trainingStatus: LoraTrainingStatus;
  triggerWord: string;
  trainer?: string | null;
  currentPhase?: string | null;
  progress?: number | null;
  progressMessage?: string | null;
  etaSeconds?: number | null;
  claimedByWorker?: string | null;
  requeueCount: number;
  createdAt: string;
  claimedAt?: string | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

// ---- Queries ----

export function useEntityLoraModels(entityId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<LoraModelSummaryDto[]>({
    enabled: Boolean(entityId),
    queryKey: ['entity-lora', entityId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/entities/${entityId}/lora`)
        .json<ApiEnvelope<LoraModelSummaryDto[]>>();
      return resp.data ?? [];
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (
        data?.some(
          (m) =>
            m.trainingStatus === 'training' || m.trainingStatus === 'queued',
        )
      )
        return 10_000;
      return false;
    },
  });
}

export function useLoraModelDetail(
  entityId: string | undefined,
  loraId: string | undefined,
) {
  const { getToken } = useAuth();
  return useQuery<LoraModelDto>({
    enabled: Boolean(entityId) && Boolean(loraId),
    queryKey: ['lora-detail', entityId, loraId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/entities/${entityId}/lora/${loraId}`)
        .json<ApiEnvelope<LoraModelDto>>();
      return resp.data;
    },
  });
}

export function useTrainingQueue(filters: {
  status?: string;
  entityType?: string;
  page?: number;
  pageSize?: number;
}) {
  const { getToken } = useAuth();
  return useQuery<{ items: TrainingQueueItemDto[]; total: number }>({
    queryKey: ['training-queue', filters],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const search = new URLSearchParams();
      if (filters.status) search.set('status', filters.status);
      if (filters.entityType) search.set('entity_type', filters.entityType);
      search.set('page', String(filters.page ?? 1));
      search.set('page_size', String(filters.pageSize ?? 50));
      const resp = await client
        .get(`api/v2/training-queue?${search.toString()}`)
        .json<ApiEnvelope<TrainingQueueItemDto[]>>();
      return { items: resp.data ?? [], total: resp.pagination?.total ?? 0 };
    },
    refetchInterval: 15_000,
  });
}

// ---- Mutations ----

export function useQueueTraining(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: QueueTrainingRequest) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/entities/${entityId}/lora`, { json: input })
        .json<ApiEnvelope<LoraModelSummaryDto>>();
      return resp.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity-lora', entityId] });
      void qc.invalidateQueries({ queryKey: ['training-queue'] });
    },
  });
}

export function useCancelTraining(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (loraId: string) => {
      const client = createApiClient(getToken);
      await client.post(`api/v2/entities/${entityId}/lora/${loraId}/cancel`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity-lora', entityId] });
      void qc.invalidateQueries({ queryKey: ['training-queue'] });
    },
  });
}

export function useRequeueTraining(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (loraId: string) => {
      const client = createApiClient(getToken);
      await client.post(`api/v2/entities/${entityId}/lora/${loraId}/requeue`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity-lora', entityId] });
      void qc.invalidateQueries({ queryKey: ['training-queue'] });
    },
  });
}

export interface TestLoraResult {
  imageUrl: string;
  provider: string;
  prompt: string;
}

export function useTestLora(entityId: string) {
  const { getToken } = useAuth();
  return useMutation({
    mutationFn: async ({
      loraId,
      prompt,
      aspectRatio,
    }: {
      loraId: string;
      prompt: string;
      aspectRatio?: string;
    }) => {
      const client = createApiClient(getToken);
      const resp = await client
        .post(`api/v2/entities/${entityId}/lora/${loraId}/test`, {
          json: { prompt, aspectRatio },
          timeout: 180_000,
        })
        .json<ApiEnvelope<TestLoraResult>>();
      return resp.data;
    },
  });
}

export function useSetLoraPriority(entityId: string) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      loraId,
      priority,
    }: {
      loraId: string;
      priority: number;
    }) => {
      const client = createApiClient(getToken);
      await client.put(`api/v2/entities/${entityId}/lora/${loraId}/priority`, {
        json: { priority },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entity-lora', entityId] });
      void qc.invalidateQueries({ queryKey: ['training-queue'] });
    },
  });
}
